// Master Apps · Lobby da apresentação — monta o recorte ANTES de projetar.
//
// Clicar em "Apresentação" no menu não joga mais direto no deck: cai aqui, uma
// tela com o chrome padrão do produto. O apresentador escolhe
// período/situação/área/tipo/criticidade com calma, vê quantos slides o recorte
// rende e só então clica "Iniciar apresentação" — que abre o deck já em tela
// cheia (o clique carrega a ativação de usuário que o fullscreen exige).
//
// Os filtros são o MESMO estado do deck (`useDeckFilters`, passado pronto pela
// página): o que se monta aqui é o que se apresenta e o que sai no PDF.
//
// A tela é de LARGURA INTEIRA, como o Inventário: era um card centralizado de
// 780px com cinco grupos de pílulas empilhados, e a lista de plataformas — o
// que se veio olhar — só aparecia depois de rolar. Agora os filtros são
// dropdowns numa linha só (com as quantidades dentro) e o resto da tela é a
// TABELA do recorte, com as colunas que a pessoa precisa para decidir quem vai
// ao ar: área, tipo, situação, criticidade, responsável, dias com acesso e
// última atualização.
//
// Filtro e marcação COMPÕEM: o filtro define o universo, a caixinha da linha
// subtrai dentro dele. Por padrão vai tudo o que o filtro deixou.
import { formatAppCode } from '@masterapps/shared';
import { Fragment, useCallback, useId, useRef, useState } from 'react';
import { AppPhoto, AppTypeTag, CriticalityBadge } from '@/components/app-visuals';
import { IconSearch, IconX } from '@/components/icons';
import { ButtonGhost, FilterChip, Sidebar, TopBar } from '@/components/primitives';
import {
  type DeckFilters,
  deckSlidePlan,
  diasDoMesDeColeta,
} from '@/components/screens/presentation-screen';
import { SelectMenu } from '@/components/select-menu';
import { FiltroDropdown, GatilhoFiltro, PainelFlutuante } from '@/components/ui/filtro-dropdown';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import type { App } from '@/lib/catalog-config';
import {
  DECK_MONTHS_PT,
  type DeckColunaOrdenavel,
  type DeckOrdenacao,
  type DeckPeriod,
  deckAreaOf,
  deckOrdenarPorColuna,
  deckPeriodTitle,
  deckStatusOf,
} from '@/lib/deck';
import { formatDateBR } from '@/lib/format';
import {
  fontMono,
  fontSans,
  makePalette,
  numeroTabular,
  rotuloCaixaAlta,
  type ScreenProps,
} from '@/lib/theme';

// ── Período ─────────────────────────────────────────────────────────────────

/** "2026" no modo ano; "Jan–Jun 2026" ou "Out 2025–Mar 2026" no intervalo. */
function resumoDoPeriodo(period: DeckPeriod): string {
  if (period.mode === 'year') return String(period.year);
  const mes = (m: number) => DECK_MONTHS_PT[m - 1]?.slice(0, 3) ?? '';
  const { start, end } = period;
  if (start.year === end.year) {
    return start.month === end.month
      ? `${mes(start.month)} ${start.year}`
      : `${mes(start.month)}–${mes(end.month)} ${start.year}`;
  }
  return `${mes(start.month)} ${start.year}–${mes(end.month)} ${end.year}`;
}

/**
 * Dropdown de mês/ano do intervalo — SelectMenu próprio no lugar do <select>
 * nativo: a lista do nativo é desenhada pelo sistema e saía com cara de HTML
 * padrão no meio do painel.
 */
function PeriodSelect({
  value,
  onChange,
  options,
  label,
  dark,
  accent,
}: Readonly<{
  value: number;
  onChange: (v: number) => void;
  options: Array<{ value: number; label: string }>;
  label: string;
  dark: boolean;
  accent: string;
}>) {
  const p = makePalette(dark);
  return (
    <SelectMenu
      value={value}
      onChange={onChange}
      options={options}
      label={label}
      tone={{
        bg: p.inputBg,
        text: p.text,
        border: p.border,
        borderActive: `${accent}66`,
        panelBg: p.surface,
        panelBorder: p.border,
        optionHover: p.surface2,
        optionSelected: accent,
        muted: p.muted,
        height: 30,
        fontSize: 12,
        radius: 7,
      }}
    />
  );
}

/**
 * Filtro de PERÍODO da barra: mesmo gatilho e painel dos outros filtros, com
 * conteúdo próprio — chips de ano (com a contagem facetada) e os De/Até do
 * intervalo de meses. Não é multisseleção, por isso não usa o FiltroDropdown.
 */
function FiltroPeriodo({
  filters,
  dark,
  accent,
}: Readonly<{ filters: DeckFilters; dark: boolean; accent: string }>) {
  const p = makePalette(dark);
  const { rawPeriod, setRawPeriod, period, periodActive, yearOptions } = filters;
  const [open, setOpen] = useState(false);
  const gatilhoRef = useRef<HTMLButtonElement | null>(null);
  const painelId = useId();
  const fechar = useCallback(() => setOpen(false), []);

  // Rascunho dos selects De/Até — no modo ano, o intervalo equivalente é
  // jan–dez dele (mesma regra do PeriodGroup do deck).
  const range =
    rawPeriod.mode === 'range'
      ? rawPeriod
      : {
          mode: 'range' as const,
          start: { year: rawPeriod.year, month: 1 },
          end: { year: rawPeriod.year, month: 12 },
        };
  const setPart = (part: 'start' | 'end', patch: Partial<{ year: number; month: number }>) =>
    setRawPeriod({
      mode: 'range',
      start: part === 'start' ? { ...range.start, ...patch } : range.start,
      end: part === 'end' ? { ...range.end, ...patch } : range.end,
    });

  const monthOptions = DECK_MONTHS_PT.map((m, i) => ({ value: i + 1, label: m }));
  const yearChoices = yearOptions.map((y) => ({ value: y.year, label: String(y.year) }));
  const rotulo = { ...rotuloCaixaAlta, fontSize: 9.5, fontWeight: 700, color: p.muted } as const;

  return (
    <>
      <GatilhoFiltro
        label="Período"
        resumo={resumoDoPeriodo(period)}
        ativo={periodActive}
        open={open}
        onClick={() => setOpen((v) => !v)}
        dark={dark}
        accent={accent}
        gatilhoRef={gatilhoRef}
        controla={painelId}
      />
      <PainelFlutuante
        open={open}
        onClose={fechar}
        gatilhoRef={gatilhoRef}
        dark={dark}
        id={painelId}
        label="Período"
        minWidth={300}
        maxWidth={420}
      >
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ ...rotulo, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Ano
              <span
                style={{ marginLeft: 8, fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}
              >
                última atualização dentro do período
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {yearOptions.map((y) => (
                <FilterChip
                  key={y.year}
                  label={String(y.year)}
                  count={y.count}
                  active={rawPeriod.mode === 'year' && rawPeriod.year === y.year}
                  dark={dark}
                  accent={accent}
                  onClick={() => setRawPeriod({ mode: 'year', year: y.year })}
                />
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...rotulo, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Intervalo de meses
              {rawPeriod.mode === 'range' && (
                <span style={{ marginLeft: 8, color: accent, letterSpacing: '0.08em' }}>ativo</span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 8,
              }}
            >
              <span style={{ fontSize: 11.5, fontWeight: 600, color: p.muted }}>De</span>
              <PeriodSelect
                label="Mês inicial"
                value={range.start.month}
                onChange={(v) => setPart('start', { month: v })}
                options={monthOptions}
                dark={dark}
                accent={accent}
              />
              <PeriodSelect
                label="Ano inicial"
                value={range.start.year}
                onChange={(v) => setPart('start', { year: v })}
                options={yearChoices}
                dark={dark}
                accent={accent}
              />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: p.muted }}>até</span>
              <PeriodSelect
                label="Mês final"
                value={range.end.month}
                onChange={(v) => setPart('end', { month: v })}
                options={monthOptions}
                dark={dark}
                accent={accent}
              />
              <PeriodSelect
                label="Ano final"
                value={range.end.year}
                onChange={(v) => setPart('end', { year: v })}
                options={yearChoices}
                dark={dark}
                accent={accent}
              />
            </div>
          </div>
        </div>
      </PainelFlutuante>
    </>
  );
}

// ── Tabela ──────────────────────────────────────────────────────────────────

/**
 * Quadradinho de marcação. Mesmo desenho do `multi-select` do produto (16px,
 * raio 4, preenchido no accent) para a tabela não parecer um controle novo.
 */
function CaixaMarcar({
  on,
  dark,
  accent,
  size = 16,
}: Readonly<{ on: boolean; dark: boolean; accent: string; size?: number }>) {
  const p = makePalette(dark);
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        background: on ? accent : 'transparent',
        border: `1.5px solid ${on ? accent : p.borderStrong}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#fff',
      }}
    >
      {on && (
        <svg
          width={size - 5}
          height={size - 5}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  );
}

/** Punho de arrasto — a alça que diz "esta linha se move". */
function PunhoDeArrasto({
  rotulo,
  dark,
  onMover,
}: Readonly<{ rotulo: string; dark: boolean; onMover: (delta: -1 | 1) => void }>) {
  const p = makePalette(dark);
  return (
    <span
      // Arrastar é gesto de mouse; sem teclado a reordenação ficaria fora do
      // alcance de quem navega por Tab. Setas movem uma posição por vez.
      role="button"
      tabIndex={0}
      aria-label={`Mover ${rotulo}`}
      title="Arraste para reordenar · ↑ ↓ movem uma posição"
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          onMover(-1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          onMover(1);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 28,
        cursor: 'grab',
        color: p.placeholder,
      }}
    >
      <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor" aria-hidden="true">
        <title>Alça de arrasto</title>
        {[2, 7, 12].map((y) => (
          <Fragment key={y}>
            <circle cx="1.6" cy={y} r="1.3" />
            <circle cx="6.4" cy={y} r="1.3" />
          </Fragment>
        ))}
      </svg>
    </span>
  );
}

/**
 * Colunas da tabela e a largura de cada uma. `null` = a coluna elástica (a
 * Aplicação fica com o que sobrar). Percentuais para as duas de texto livre
 * que também podem encolher; pixels para as de conteúdo fixo (selo, data,
 * número). Com `tableLayout: fixed` é o `<colgroup>` que manda — a soma das
 * fixas em 1280px de janela (sidebar 220 + 2×24 de margem = 1012 úteis) deixa
 * ~380px para o nome da aplicação, o suficiente para o nome mais longo do
 * portfólio sem reticências.
 */
interface Coluna {
  id: 'punho' | 'marca' | 'app' | 'area' | 'tipo' | 'situacao' | 'crit' | 'resp' | 'dias' | 'atual';
  rotulo: string;
  largura: number | string | null;
  ordena?: DeckColunaOrdenavel;
  /** Alinhamento do conteúdo (número à direita, selo centralizado). */
  alinhar?: 'center' | 'right';
  /** Em que faixas de viewport a coluna aparece. */
  em: readonly Viewport['bp'][];
}

const COLUNAS: readonly Coluna[] = [
  { id: 'punho', rotulo: '', largura: 26, em: ['mobile', 'tablet', 'desktop'] },
  { id: 'marca', rotulo: '', largura: 36, em: ['mobile', 'tablet', 'desktop'] },
  {
    id: 'app',
    rotulo: 'Aplicação',
    largura: null,
    ordena: 'nome',
    em: ['mobile', 'tablet', 'desktop'],
  },
  {
    id: 'area',
    rotulo: 'Área solicitante',
    largura: '14%',
    ordena: 'area',
    em: ['tablet', 'desktop'],
  },
  { id: 'tipo', rotulo: 'Tipo', largura: 124, ordena: 'tipo', em: ['desktop'] },
  {
    id: 'situacao',
    rotulo: 'Situação',
    largura: 156,
    ordena: 'situacao',
    em: ['mobile', 'tablet', 'desktop'],
  },
  { id: 'crit', rotulo: 'Criticidade', largura: 98, ordena: 'criticidade', em: ['desktop'] },
  { id: 'resp', rotulo: 'Responsável', largura: '13%', ordena: 'responsavel', em: ['desktop'] },
  {
    id: 'dias',
    rotulo: 'Dias úteis com acesso',
    largura: 92,
    ordena: 'acessos',
    alinhar: 'right',
    em: ['tablet', 'desktop'],
  },
  {
    id: 'atual',
    rotulo: 'Atualizada',
    largura: 100,
    ordena: 'atualizada',
    alinhar: 'right',
    em: ['desktop'],
  },
];

/**
 * "02/22": dias ÚTEIS do mês de coleta com acesso, sobre os dias úteis do mês —
 * a MESMA conta do carimbo do slide (`diasDoMesDeColeta`). "—" quando a coleta
 * nunca rodou ou falhou; o motivo vai para o tooltip.
 */
function diasUteisComAcesso(app: App): { valor: string; titulo: string } {
  if (app.acessosErro) return { valor: '—', titulo: `Última coleta falhou: ${app.acessosErro}` };
  if (!app.acessosColetadoEm || typeof app.acessosDiasMes !== 'number') {
    return { valor: '—', titulo: 'Acessos ainda não coletados do Clerk' };
  }
  const uteis = diasDoMesDeColeta(app.acessosColetadoEm);
  return {
    valor: `${String(app.acessosDiasMes).padStart(2, '0')}/${uteis}`,
    titulo: `${app.acessosDiasMes} de ${uteis} dias úteis do mês com pelo menos um acesso`,
  };
}

/** `aria-sort` do <th>: só a coluna ativa anuncia direção. */
function ordenacaoAria(
  coluna: Coluna,
  ordenacao: DeckOrdenacao | null,
): 'ascending' | 'descending' | undefined {
  if (!coluna.ordena || ordenacao?.col !== coluna.ordena) return undefined;
  return ordenacao.dir === 1 ? 'ascending' : 'descending';
}

/** Cabeçalho clicável: ↕ apagado, ▲/▼ no acento quando é a coluna ativa. */
function CabecalhoOrdenavel({
  coluna,
  ordenacao,
  onOrdenar,
  accent,
}: Readonly<{
  coluna: Coluna;
  ordenacao: DeckOrdenacao | null;
  onOrdenar: (col: DeckColunaOrdenavel) => void;
  accent: string;
}>) {
  const ativo = coluna.ordena !== undefined && ordenacao?.col === coluna.ordena;
  let seta = '↕';
  if (ativo) seta = ordenacao?.dir === 1 ? '▲' : '▼';
  if (!coluna.ordena) return <span>{coluna.rotulo}</span>;
  const col = coluna.ordena;
  return (
    <button
      type="button"
      onClick={() => onOrdenar(col)}
      title={`Ordenar por ${coluna.rotulo}`}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: coluna.alinhar === 'right' ? 'flex-end' : 'flex-start',
        gap: 4,
        maxWidth: '100%',
        font: 'inherit',
        color: ativo ? accent : 'inherit',
        textTransform: 'inherit',
        letterSpacing: 'inherit',
        fontWeight: 'inherit',
        textAlign: coluna.alinhar === 'right' ? 'right' : 'left',
        whiteSpace: 'normal',
        lineHeight: 1.25,
      }}
    >
      {coluna.rotulo}
      <span style={{ opacity: ativo ? 1 : 0.35, fontSize: 9, flexShrink: 0 }}>{seta}</span>
    </button>
  );
}

/**
 * Tabela do recorte — UMA LINHA POR PLATAFORMA, arrastável para reordenar.
 *
 * A ordem das linhas é a do deck (por área, ou a manual que o apresentador
 * montou arrastando). Clicar no cabeçalho ordena SÓ A LEITURA: a ordem manual
 * continua guardada e volta a valer assim que a pessoa arrasta uma linha —
 * senão o arrasto pareceria não ter efeito, porque a coluna reordenaria tudo.
 *
 * A tabela mostra o RECORTE (não o portfólio): filtrar é a forma grossa de
 * escolher, marcar é a fina. Quem o filtro já tirou não aparece aqui para não
 * haver dois lugares dizendo a mesma coisa.
 */
function TabelaDePlataformas({
  recorteApps,
  marcada,
  alternarMarcada,
  reordenar,
  ordenacao,
  setOrdenacao,
  dark,
  accent,
}: Readonly<{
  recorteApps: App[];
  marcada: (id: string) => boolean;
  alternarMarcada: (id: string) => void;
  /** Solta a plataforma arrastada na posição da plataforma alvo. */
  reordenar: (idArrastado: string, idAlvo: string) => void;
  ordenacao: DeckOrdenacao | null;
  setOrdenacao: (o: DeckOrdenacao | null) => void;
  dark: boolean;
  accent: string;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);

  const colunas = COLUNAS.filter((c) => c.em.includes(vp.bp));
  const linhas = deckOrdenarPorColuna(recorteApps, ordenacao);

  // Arrastar DESLIGA a ordenação por coluna: a posição em que a linha cai é
  // relativa à ordem manual, e é ela que a tela precisa mostrar em seguida.
  const soltar = (idArrastado: string, idAlvo: string) => {
    setOrdenacao(null);
    reordenar(idArrastado, idAlvo);
  };
  const moverPorTeclado = (id: string, delta: -1 | 1) => {
    const i = linhas.findIndex((a) => a.id === id);
    const vizinho = linhas[i + delta];
    if (vizinho) soltar(id, vizinho.id);
  };
  // Clique no cabeçalho: crescente → decrescente → volta à ordem manual.
  const ordenarPor = (col: DeckColunaOrdenavel) => {
    if (ordenacao?.col !== col) setOrdenacao({ col, dir: 1 });
    else if (ordenacao.dir === 1) setOrdenacao({ col, dir: -1 });
    else setOrdenacao(null);
  };

  const celula = {
    padding: '0 10px',
    height: 42,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderTop: `1px solid ${p.border}`,
    fontSize: 12.5,
    verticalAlign: 'middle',
  } as const;

  return (
    <table
      style={{
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontFamily: fontSans,
        color: p.text,
      }}
    >
      <colgroup>
        {colunas.map((c) => (
          <col key={c.id} style={{ width: c.largura ?? undefined }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {colunas.map((c) => (
            <th
              key={c.id}
              scope="col"
              aria-sort={ordenacaoAria(c, ordenacao)}
              style={{
                ...rotuloCaixaAlta,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: p.surface2,
                borderBottom: `1px solid ${p.border}`,
                padding: '9px 10px',
                fontSize: 9.5,
                fontWeight: 700,
                color: p.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                textAlign: c.alinhar ?? 'left',
                whiteSpace: 'normal',
                lineHeight: 1.25,
                overflow: 'hidden',
              }}
            >
              <CabecalhoOrdenavel
                coluna={c}
                ordenacao={ordenacao}
                onOrdenar={ordenarPor}
                accent={accent}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((a) => {
          const on = marcada(a.id);
          const s = deckStatusOf(a);
          const area = deckAreaOf(a);
          const dias = diasUteisComAcesso(a);
          const alvo = sobre === a.id && arrastando !== null && arrastando !== a.id;
          const apagado = on ? p.text : p.placeholder;
          return (
            // O arrasto mora na LINHA inteira, não só no punho: quem já
            // entendeu o gesto pega a linha de qualquer ponto, e o punho
            // continua ali dizendo que ela se move. O clique na caixinha segue
            // funcionando — arrasto só começa com movimento.
            <tr
              key={a.id}
              data-app-id={a.id}
              data-app-nome={a.name}
              draggable
              onDragStart={(e: React.DragEvent) => {
                setArrastando(a.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', a.id);
              }}
              onDragEnd={() => {
                setArrastando(null);
                setSobre(null);
              }}
              onDragOver={(e: React.DragEvent) => {
                // Sem o preventDefault o navegador recusa o solte e o onDrop
                // nunca dispara — é o contrato do HTML5 drag.
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (sobre !== a.id) setSobre(a.id);
              }}
              onDragLeave={() => setSobre((v) => (v === a.id ? null : v))}
              onDrop={(e: React.DragEvent) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain') || arrastando;
                if (id && id !== a.id) soltar(id, a.id);
                setArrastando(null);
                setSobre(null);
              }}
              style={{
                // Linha de alvo em cima: mostra ONDE a plataforma vai cair.
                boxShadow: alvo ? `inset 0 2px 0 0 ${accent}` : 'none',
                opacity: arrastando === a.id ? 0.45 : 1,
                background: on ? 'transparent' : p.surface2,
              }}
            >
              {colunas.map((c) => {
                switch (c.id) {
                  case 'punho':
                    return (
                      <td key={c.id} style={{ ...celula, padding: 0 }}>
                        <PunhoDeArrasto
                          rotulo={a.name}
                          dark={dark}
                          onMover={(delta) => moverPorTeclado(a.id, delta)}
                        />
                      </td>
                    );
                  case 'marca':
                    return (
                      <td key={c.id} style={{ ...celula, padding: '0 6px 0 4px' }}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={on}
                          aria-label={a.name}
                          title={on ? 'Tirar da apresentação' : 'Devolver à apresentação'}
                          onClick={() => alternarMarcada(a.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 26,
                            height: 26,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          <CaixaMarcar on={on} dark={dark} accent={accent} />
                        </button>
                      </td>
                    );
                  case 'app':
                    return (
                      <td key={c.id} style={celula}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}
                        >
                          <span style={{ opacity: on ? 1 : 0.5, display: 'flex', flexShrink: 0 }}>
                            <AppPhoto app={a} size={26} dark={dark} />
                          </span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              title={a.name}
                              style={{
                                fontWeight: 600,
                                // Desmarcada continua legível, mas recuada: a
                                // tabela é a lista de quem VAI, não um
                                // cemitério de quem ficou.
                                color: apagado,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                              }}
                            >
                              {a.name}
                            </div>
                            <div
                              style={{
                                fontFamily: fontMono,
                                fontSize: 10,
                                color: p.placeholder,
                                marginTop: 1,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatAppCode(a.code)}
                            </div>
                          </div>
                        </div>
                      </td>
                    );
                  case 'area':
                    return (
                      <td
                        key={c.id}
                        title={area}
                        style={{ ...celula, color: on ? p.textMuted : p.placeholder }}
                      >
                        {area}
                      </td>
                    );
                  case 'tipo':
                    return (
                      <td key={c.id} style={{ ...celula, opacity: on ? 1 : 0.55 }}>
                        <AppTypeTag type={a.type} dark={dark} />
                      </td>
                    );
                  case 'situacao':
                    return (
                      <td key={c.id} style={{ ...celula, opacity: on ? 1 : 0.55 }}>
                        <span
                          title={s.label}
                          style={{
                            ...rotuloCaixaAlta,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            maxWidth: '100%',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            color: s.color,
                          }}
                        >
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: 999,
                              background: s.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.label}
                          </span>
                        </span>
                      </td>
                    );
                  case 'crit':
                    return (
                      <td key={c.id} style={{ ...celula, opacity: on ? 1 : 0.55 }}>
                        <CriticalityBadge level={a.criticality} dark={dark} />
                      </td>
                    );
                  case 'resp':
                    return (
                      <td
                        key={c.id}
                        title={a.owner}
                        style={{ ...celula, color: on ? p.textMuted : p.placeholder }}
                      >
                        {a.owner}
                      </td>
                    );
                  case 'dias':
                    return (
                      <td
                        key={c.id}
                        title={dias.titulo}
                        style={{
                          ...celula,
                          ...numeroTabular,
                          textAlign: 'right',
                          color: dias.valor === '—' ? p.placeholder : on ? p.text : p.placeholder,
                          fontWeight: 600,
                        }}
                      >
                        {dias.valor}
                      </td>
                    );
                  case 'atual':
                    return (
                      <td
                        key={c.id}
                        style={{
                          ...celula,
                          ...numeroTabular,
                          textAlign: 'right',
                          color: on ? p.textMuted : p.placeholder,
                        }}
                      >
                        {a.lastUpdate ? formatDateBR(a.lastUpdate) : '—'}
                      </td>
                    );
                  default:
                    return <td key={c.id} style={celula} />;
                }
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Tela ────────────────────────────────────────────────────────────────────

export function ScreenPresentationLobby({
  dark,
  accent,
  apps,
  filters,
  role = 'admin',
  onStart,
}: Readonly<
  ScreenProps & {
    apps: App[];
    filters: DeckFilters;
    role?: 'admin' | 'collab';
    /** Inicia o deck (a página troca para a apresentação em tela cheia). */
    onStart: () => void;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const {
    query,
    setQuery,
    q,
    period,
    groups,
    recorteApps,
    deckApps,
    desmarcadasNoRecorte,
    marcada,
    alternarMarcada,
    marcarTodas,
    activeFilters,
    clearFilters,
    reordenar,
    ordemPersonalizada,
    restaurarOrdem,
  } = filters;
  // Ordenação por coluna da tabela: estado da TELA, não do deck (ver
  // `deckOrdenarPorColuna`). Não persiste e o arrasto a desliga.
  const [ordenacao, setOrdenacao] = useState<DeckOrdenacao | null>(null);

  const heading = deckPeriodTitle(period);
  // Contagem pelo MESMO plano que o deck monta — desde o slide de Projeto,
  // "plataformas + 3" mentia para menos.
  const totalSlides = deckSlidePlan(deckApps).length;
  const idsDoRecorte = recorteApps.map((a) => a.id);
  const canStart = deckApps.length > 0;
  // Zero plataformas tem duas causas MUITO diferentes, e a saída de cada uma é
  // outra: dizer só "não dá para iniciar" deixaria o usuário caçando o motivo.
  let motivoBloqueio = '';
  if (recorteApps.length === 0) {
    motivoBloqueio =
      'Os filtros não deixaram nenhuma plataforma no recorte — troque o período ou limpe os filtros.';
  } else if (deckApps.length === 0) {
    motivoBloqueio = `Todas as ${recorteApps.length} plataformas do recorte estão desmarcadas — marque ao menos uma para apresentar.`;
  }

  // Rótulos da barra: os do hook falam a língua do deck ("Status"); a tela
  // usa a do produto ("Situação"), a mesma do Inventário.
  const rotuloDoGrupo: Record<string, string> = { status: 'Situação' };

  const botaoIniciar = (
    <button
      type="button"
      onClick={onStart}
      disabled={!canStart}
      title={canStart ? 'Iniciar em tela cheia' : motivoBloqueio}
      style={{
        height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 34 }),
        padding: '0 16px',
        borderRadius: 7,
        border: 'none',
        background: canStart ? accent : p.surface3,
        color: canStart ? '#fff' : p.muted,
        fontSize: 12.5,
        fontWeight: 700,
        fontFamily: fontSans,
        cursor: canStart ? 'pointer' : 'not-allowed',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
      }}
    >
      {/* play */}
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <polygon points="6 3 21 12 6 21" />
      </svg>
      Iniciar apresentação
    </button>
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="presentation" dark={dark} accent={accent} role={role} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Apresentação"
          subtitle={`${deckApps.length} de ${apps.length} ${
            apps.length === 1 ? 'plataforma' : 'plataformas'
          } no deck · ${totalSlides} slides · capa “${heading.title}${
            heading.sub ? ` · ${heading.sub}` : ''
          }”`}
          right={botaoIniciar}
        />

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            // Mesma margem do Inventário: 24 no desktop, menos onde a largura
            // vale mais que a folga.
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
          }}
        >
          {/* Barra de filtros: busca + dropdowns numa linha; embrulha em duas
              só quando a janela não dá (abaixo de ~1100px). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 32,
                padding: '0 10px',
                flex: byViewport(vp, { mobile: '1 1 100%', desktop: '1 1 200px' }),
                minWidth: byViewport(vp, { mobile: 0, desktop: 180 }),
                maxWidth: byViewport(vp, { mobile: '100%', desktop: 320 }),
                background: p.inputBg,
                border: `1px solid ${p.border}`,
                borderRadius: 7,
              }}
            >
              <span style={{ color: p.muted, display: 'flex' }}>
                <IconSearch size={13} />
              </span>
              <input
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                placeholder="Nome, área, responsável ou descrição…"
                aria-label="Buscar plataformas"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12.5,
                  color: p.text,
                  fontFamily: fontSans,
                }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Limpar busca"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: p.muted,
                    cursor: 'pointer',
                    display: 'flex',
                    padding: 0,
                  }}
                >
                  <IconX size={12} />
                </button>
              )}
            </div>

            {groups.map((g) => (
              <FiltroDropdown
                key={g.id}
                label={rotuloDoGrupo[g.id] ?? g.label}
                options={g.options}
                selected={g.selected}
                onToggle={g.toggle}
                // `toggle` usa atualização funcional, então desligar todas as
                // selecionadas no mesmo tique dá o resultado certo.
                onClear={() => {
                  for (const id of g.selected) g.toggle(id);
                }}
                dark={dark}
                accent={accent}
              />
            ))}

            <FiltroPeriodo filters={filters} dark={dark} accent={accent} />

            {(activeFilters > 0 || q) && (
              <ButtonGhost dark={dark} onClick={clearFilters}>
                <IconX size={12} /> Limpar filtros
              </ButtonGhost>
            )}
          </div>

          {/* Linha de resumo + ações em lote da tabela */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  ...numeroTabular,
                  fontSize: 20,
                  fontWeight: 800,
                  color: canStart ? p.text : accent,
                  lineHeight: 1,
                }}
              >
                {deckApps.length}
              </span>
              <span style={{ fontSize: 12.5, color: p.muted }}>
                {deckApps.length === 1 ? 'plataforma' : 'plataformas'} no deck
                {recorteApps.length !== apps.length &&
                  ` · ${recorteApps.length} no recorte dos filtros`}
                {desmarcadasNoRecorte > 0 &&
                  ` · ${desmarcadasNoRecorte} ${desmarcadasNoRecorte === 1 ? 'desmarcada' : 'desmarcadas'}`}
              </span>
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: p.placeholder }}>
              {ordemPersonalizada
                ? 'ordem personalizada · arraste para mudar'
                : 'arraste para ordenar · a ordem vale no deck e no PDF'}
            </span>
            {recorteApps.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Só aparece quando há o que desfazer: botão de restaurar
                    sempre visível sugeriria que a ordem padrão não está valendo. */}
                {ordenacao && (
                  <ButtonGhost dark={dark} onClick={() => setOrdenacao(null)}>
                    Ordem do deck
                  </ButtonGhost>
                )}
                {ordemPersonalizada && (
                  <ButtonGhost dark={dark} onClick={restaurarOrdem}>
                    Ordem por área
                  </ButtonGhost>
                )}
                <ButtonGhost dark={dark} onClick={() => marcarTodas(idsDoRecorte, true)}>
                  Marcar todas
                </ButtonGhost>
                <ButtonGhost dark={dark} onClick={() => marcarTodas(idsDoRecorte, false)}>
                  Nenhuma
                </ButtonGhost>
              </div>
            )}
          </div>

          {/* Botão apagado sem explicação é armadilha: a linha diz por que não
              dá para começar e o que fazer para destravar. */}
          {motivoBloqueio && (
            <div
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                fontSize: 11.5,
                fontWeight: 600,
                color: accent,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: accent,
                  flexShrink: 0,
                }}
              />
              {motivoBloqueio}
            </div>
          )}

          {/* Tabela do recorte */}
          <div
            style={{
              background: p.surface,
              border: `1px solid ${p.border}`,
              borderRadius: 10,
              // Rola só na vertical (e quem rola é a página): com
              // `tableLayout: fixed` e largura 100% a tabela nunca passa da
              // caixa, e um `overflow: hidden` aqui anularia o cabeçalho
              // grudado no topo.
              overflowX: 'hidden',
            }}
          >
            {recorteApps.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: p.muted, fontSize: 13 }}>
                Os filtros não deixaram nenhuma plataforma para escolher.
              </div>
            ) : (
              <TabelaDePlataformas
                recorteApps={recorteApps}
                marcada={marcada}
                alternarMarcada={alternarMarcada}
                reordenar={reordenar}
                ordenacao={ordenacao}
                setOrdenacao={setOrdenacao}
                dark={dark}
                accent={accent}
              />
            )}
          </div>

          {/* Dica de uso */}
          <div
            style={{
              marginTop: 12,
              fontSize: 11.5,
              color: p.muted,
              fontFamily: fontSans,
              textAlign: 'center',
            }}
          >
            O deck abre em tela cheia · setas navegam · Esc sai · o PDF sai do próprio deck
          </div>
        </div>
      </div>
    </div>
  );
}
